import { Injectable } from '@angular/core';
import { ItemService } from '../services/item.service';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { pipe, withLatestFrom } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { StoredFile } from '../../models/StoredFile';

interface ItemAttachmentsState {
  itemId: number;
  attachments: StoredFile[];
  loading: boolean;
  error: string;
}

const defaultState = {
  itemId: null,
  attachments: [],
  loading: false,
  error: null
};

@Injectable()
export class ItemAttachmentsStoreService extends ComponentStore<ItemAttachmentsState> {
  constructor(private itemService: ItemService) {
    super(defaultState);
    this.select((state) => state.itemId).subscribe((itemId) => {
      if (itemId) {
        this.loadAttachments(itemId);
      }
    });
  }

  readonly setItemId = this.updater((state, itemId: number) => ({
    ...state,
    loading: true,
    itemId
  }));
  readonly loading$ = this.select((state) => state.loading);
  readonly attachments$ = this.select((state) => state.attachments);

  private loadAttachments(itemId: number) {
    this.itemService.getItemAttachments(itemId).subscribe((attachments) => {
      this.patchState({
        attachments,
        loading: false
      });
    });
  }

  private readonly appendAttachment = this.updater<StoredFile>(
    (state, attachment) => ({
      ...state,
      attachments: [...state.attachments, attachment]
    })
  );

  private readonly setError = this.updater((state, error: any) => ({
    ...state,
    error
  }));

  readonly addAttachment = this.effect<{
    file: File;
    description: string;
    onSuccess: () => void;
  }>(
    pipe(
      withLatestFrom(this.select((state) => state.itemId)),
      mergeMap(([{ onSuccess, ...upload }, itemId]) =>
        this.itemService.addItemAttachment(itemId, upload).pipe(
          tapResponse(
            (attachment) => this.appendAttachment(attachment),
            (error) => this.setError(error)
          ),
          tap(() => onSuccess())
        )
      )
    )
  );

  readonly deleteAttachment = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      withLatestFrom(this.select((state) => state.itemId)),
      mergeMap(([attachmentId, itemId]) => {
        return this.itemService.deleteAttachment(itemId, attachmentId).pipe(
          tapResponse(
            () => this.loadAttachments(itemId),
            (error) => this.setError(error)
          )
        );
      })
    )
  );
}
